import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import { cn } from "@/lib/utils";

const HOVER_DELAY_MS = 1000;
/** ~45px/s — slow enough to read while the loop repeats. */
const MS_PER_PIXEL = 22;
const MIN_CYCLE_MS = 5200;
const MAX_CYCLE_MS = 22000;

function marqueeDurationMs(overflowPx: number): number {
  return Math.min(
    MAX_CYCLE_MS,
    Math.max(MIN_CYCLE_MS, overflowPx * MS_PER_PIXEL),
  );
}

interface KaiConversationTitleProps {
  title: string;
  className?: string;
  /** True while the pointer is anywhere on the history row. */
  rowHovered?: boolean;
}

/**
 * Truncated chat titles in history: after 1s row hover, scroll the full title
 * inline and loop until pointer leaves (then snap back to truncated).
 *
 * The clip box is a fixed single-line slot; only `transform` animates so rows
 * never shift padding, height, or outline.
 */
export function KaiConversationTitle({
  title,
  className,
  rowHovered = false,
}: KaiConversationTitleProps) {
  const clipRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const delayRef = useRef<number | undefined>(undefined);
  const [overflowPx, setOverflowPx] = useState(0);
  const [marquee, setMarquee] = useState(false);

  const measure = useCallback(() => {
    const clip = clipRef.current;
    const text = textRef.current;
    if (!clip || !text) return 0;
    const clipWidth = clip.clientWidth;
    const idle =
      text.classList.contains("truncate") &&
      !text.classList.contains("kai-history-title-marquee");
    if (idle) {
      text.classList.remove("w-full", "min-w-0", "truncate");
      text.classList.add("w-max", "max-w-none");
    }
    const fullWidth = text.scrollWidth;
    if (idle) {
      text.classList.remove("w-max", "max-w-none");
      text.classList.add("w-full", "min-w-0", "truncate");
    }
    const next = Math.max(0, fullWidth - clipWidth);
    setOverflowPx(next);
    if (next === 0) setMarquee(false);
    return next;
  }, []);

  useLayoutEffect(() => {
    measure();
    const clip = clipRef.current;
    if (!clip) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(clip);
    return () => ro.disconnect();
  }, [measure, title]);

  const clearDelay = useCallback(() => {
    if (delayRef.current !== undefined) {
      window.clearTimeout(delayRef.current);
      delayRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    if (!rowHovered) {
      clearDelay();
      setMarquee(false);
      return;
    }

    clearDelay();
    delayRef.current = window.setTimeout(() => {
      const next = measure();
      if (next > 0) setMarquee(true);
      delayRef.current = undefined;
    }, HOVER_DELAY_MS);

    return clearDelay;
  }, [clearDelay, measure, rowHovered]);

  const animating = marquee && overflowPx > 0;
  const style: CSSProperties | undefined = animating
    ? {
        ["--kai-title-marquee-px" as string]: `-${overflowPx}px`,
        animationDuration: `${marqueeDurationMs(overflowPx)}ms`,
      }
    : undefined;

  return (
    <div
      ref={clipRef}
      className={cn(
        "relative h-[1lh] min-w-0 overflow-hidden font-medium leading-snug",
        className,
      )}
    >
      <span
        ref={textRef}
        className={cn(
          "absolute left-0 top-0 flex h-[1lh] items-center whitespace-nowrap",
          animating
            ? "kai-history-title-marquee w-max max-w-none"
            : "w-full min-w-0 truncate",
        )}
        style={style}
        title={animating ? undefined : title}
      >
        {title}
      </span>
    </div>
  );
}
